import React, { FC, useEffect, useState } from 'react'
import MyVehicleForm from '../MyVehicleForm/MyVehicleForm.component'
import { Vehicle } from '../../types/vehicle.interface'
import { useTranslation } from 'react-i18next'
import DeleteVehicle from '../DeleteVehicle/DeleteVehicle.component'
import { useVehicle } from '../../hooks/useVehicle.hook'
import Drawer from '@components/Drawer/Drawer.component'
import { Title } from './NewVehicleDialog.style'

interface Props {
    vehicle?: Vehicle
    isOpen: boolean
    deleteLoading?: boolean
    setIsOpen: (isOpen: boolean) => void
    onSubmit?: (vehicle: Vehicle) => void
    onDeleteClick?: (vehicle: Vehicle) => void
    onError?: (error: string) => void
}

const NewVehicleDialog: FC<Props> = ({
    vehicle,
    isOpen,
    deleteLoading = false,
    setIsOpen,
    onSubmit,
    onDeleteClick,
    onError
}) => {
    const [newVehicle, setNewVehicle] = useState<Vehicle>({})
    const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false)

    const { createVehicle, updateVehicle, loading } = useVehicle()
    const { t } = useTranslation()

    const onSubmitClick = async (vehicle: Vehicle): Promise<boolean> => {
        setNewVehicle(vehicle)
        // if id, then edit vehicle
        const submitSuccess = vehicle.id ? await updateVehicle(vehicle) : await createVehicle(vehicle)
        if (submitSuccess.success) {
            if (onSubmit) onSubmit(submitSuccess.vehicle)
            setIsOpen(false)
            return true
        } else {
            if (onError) {
                onError(submitSuccess.errorCode || 'error')
            }
            return false
        }
    }

    const onCancelClick = () => {
        setTimeout(() => {
            setIsOpen(false) // Close modal after animation
            setConfirmationDialogOpen(false)
        }, 500) // Match the duration of the slideDown animation
    }

    const openDeleteConfirmation = (vehicle: Vehicle) => {
        setTimeout(() => {
            setNewVehicle(vehicle)
            setConfirmationDialogOpen(true)
        }, 500) // Match the duration of the slideDown animation
    }

    useEffect(() => {
        if (isOpen) {
            setNewVehicle(vehicle || {})
            setConfirmationDialogOpen(false)
            document.body.style.overflow = 'hidden' // Lock background scroll
        } else {
            document.body.style.overflow = 'auto' // Unlock background scroll
        }
    }, [isOpen])

    if (!isOpen) return null

    return (
        <Drawer
            isOpen={isOpen}
            content={
                confirmationDialogOpen ? (
                    <DeleteVehicle
                        loading={deleteLoading}
                        onConfirm={() => {
                            if (onDeleteClick) {
                                onDeleteClick(newVehicle)
                            }
                        }}
                    />
                ) : (
                    <>
                        <Title>{t(`profile.vehicle.${newVehicle.id ? 'editVehicleTitle' : 'addVehicleTitle'}`)}</Title>
                        <MyVehicleForm
                            inDialog
                            onSubmitClick={onSubmitClick}
                            onCancelClick={onCancelClick}
                            onDeleteClick={openDeleteConfirmation}
                            loading={loading}
                            vehicle={newVehicle}
                        />
                    </>
                )
            }
            onClose={onCancelClick}
        />
    )
}

export default NewVehicleDialog
